import React, { useState } from 'react';
import { PageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Target, CheckCircle, Lightbulb, Award, Building } from 'lucide-react';

const QRMVocabulary = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // WORLD-CLASS QRM VOCABULARY - Complete Silver Training Integration
  const vocabulary = [
    // QRM Fundamentals (Alpha + Silver)
    {
      term: "MCT (Manufacturing Critical-path Time)",
      definition: "The typical calendar time from when a customer places an order to the first delivery of that order. This determines the response time for a given product and is measured in calendar time, not just production time.",
      category: "qrm-fundamentals",
      source: "QRM Alpha & Silver Training"
    },
    {
      term: "POLCA (Paired-cell Overlapping Loops of Cards with Authorization)",
      definition: "It's a production control system designed for factories making low-volume, high-mix, or custom products — exactly where Kanban falls short. A POLCA card is a capacity signal between two consecutive work cells: cell B only pulls work from cell A when it genuinely has room to process it. No pushing half-finished jobs onto an overloaded next step — no queues, no firefighting. POLCA executes what an upstream planning system already authorized, so work flows fast, smooth, and predictable.",
      category: "qrm-fundamentals",
      source: "QRM Alpha & Silver Training"
    },
    {
      term: "Little's Law",
      definition: "Fundamental QRM principle: Doorlooptijd = WIP / Throughput (Lead Time = Work-in-Progress ÷ Throughput). Essential for understanding flow relationships and calculating system performance.",
      category: "qrm-fundamentals",
      source: "QRM Silver Training"
    },
    {
      term: "High-Mix Low-Volume Environments",
      definition: "Manufacturing environments characterized by producing a large variety of products in small quantities. This is the key target area for QRM methodology and determines VSM suitability.",
      category: "qrm-fundamentals",
      source: "QRM Silver Training"
    },
    {
      term: "Time-Based Thinking vs Cost-Thinking",
      definition: "QRM's paradigm shift from cost-focused optimization to time-focused optimization. Time-based thinking avoids suboptimization and dramatically impacts operational performance and ROI.",
      category: "qrm-philosophy",
      source: "QRM Silver Training"
    },
    {
      term: "Power of Time (Kracht van Tijd)",
      definition: "Core QRM philosophy emphasizing the importance of reducing lead times as the central goal for competitive advantage and business success.",
      category: "qrm-philosophy",
      source: "QRM Silver Training"
    },
    
    // System Dynamics (Silver Lesson 3)
    {
      term: "System Dynamics (Systeemdynamica)",
      definition: "Framework for understanding complex systems and their behavior over time. Critical for QRM implementation to understand feedback loops and system-wide improvements.",
      category: "system-dynamics",
      source: "QRM Silver Training - System Dynamics"
    },
    {
      term: "Utilization Rate (Bezettingsgraad)",
      definition: "The percentage of capacity used. QRM principle: Aim for 80-85% utilization to maintain flexibility and avoid exponential lead time increases.",
      category: "system-dynamics",
      source: "QRM Silver Training - System Dynamics"
    },
    {
      term: "Reserve Capacity (Reservecapaciteit)",
      definition: "Strategic spare capacity maintained to handle variability. The 'miraculous effect' - small reserve capacity provides disproportionately large improvements in lead time.",
      category: "system-dynamics",
      source: "QRM Silver Training - System Dynamics"
    },
    {
      term: "Variability (Variabiliteit)",
      definition: "Fluctuations in demand, processing times, or system inputs. Key concept in QRM - variability is detrimental to performance and must be managed through reserve capacity.",
      category: "system-dynamics",
      source: "QRM Silver Training - System Dynamics"
    },
    {
      term: "Nonlinear Relationships",
      definition: "In QRM systems, small changes in utilization can cause exponential changes in lead time. Formula: M = b/(1-b) where b is utilization rate.",
      category: "system-dynamics",
      source: "QRM Silver Training - System Dynamics"
    },
    {
      term: "Backlog (Achterstand)",
      definition: "The accumulation of work when utilization is high. Creates negative feedback loops that increase lead times and reduce system predictability.",
      category: "system-dynamics",
      source: "QRM Silver Training - System Dynamics"
    },
    
    // QRM Teams (Silver Lesson 4)
    {
      term: "QRM Cell (Q-CEL)",
      definition: "A self-managing, cross-trained team responsible for a complete process or product family within a cell-based manufacturing system. Essential for QRM implementation.",
      category: "qrm-teams",
      source: "QRM Silver Training - Teams"
    },
    {
      term: "Q-ROC (Quick Response Office Cell)",
      definition: "A cross-functional team focused on specific tasks or projects, often related to administrative or office tasks in QRM implementation.",
      category: "qrm-teams",
      source: "QRM Silver Training - Teams"
    },
    {
      term: "Autonomous Teams (Autonoom Team)",
      definition: "Self-managing teams with maximum autonomy to make decisions and manage their work processes. Core principle of QRM organizational design.",
      category: "qrm-teams",
      source: "QRM Silver Training - Teams"
    },
    {
      term: "Mini-Companies",
      definition: "Small, self-contained teams that operate like independent business units within the larger organization. QRM organizational structure concept.",
      category: "qrm-teams",
      source: "QRM Silver Training - Teams"
    },
    {
      term: "Two Pizza Rule",
      definition: "Jeff Bezos principle: A team should be small enough to be fed with two pizzas (7-12 members). Optimal size for QRM team effectiveness.",
      category: "qrm-teams",
      source: "QRM Silver Training - Teams"
    },
    {
      term: "Member Interchangeability",
      definition: "QRM team design principle where team members can perform multiple roles, providing flexibility and resilience through cross-training.",
      category: "qrm-teams",
      source: "QRM Silver Training - Teams"
    },
    {
      term: "Team of Teams",
      definition: "Organizational concept where multiple QRM teams work together in complex organizations, maintaining coordination while preserving team autonomy.",
      category: "qrm-teams",
      source: "QRM Silver Training - Teams"
    },
    
    // QRM Leadership (Silver Lesson 5)
    {
      term: "Boss vs Leader (Baas vs Leider)",
      definition: "QRM leadership transformation: Move from directive 'boss' mentality to empowering 'leader' approach that supports team autonomy and development.",
      category: "qrm-leadership",
      source: "QRM Silver Training - Leadership"
    },
    {
      term: "Empowering Leadership",
      definition: "Core QRM leadership style: Transitioning from directing to coaching and supporting. Enables team autonomy and fosters initiative within QRM cells.",
      category: "qrm-leadership",
      source: "QRM Silver Training - Leadership"
    },
    {
      term: "Theory X vs Theory Y Management",
      definition: "QRM prefers Theory Y: Management approach based on employee empowerment and intrinsic motivation rather than control and external motivation.",
      category: "qrm-leadership",
      source: "QRM Silver Training - Leadership"
    },
    {
      term: "Facilitative Leadership",
      definition: "QRM leadership style focused on guiding and supporting team self-management rather than direct task management.",
      category: "qrm-leadership",
      source: "QRM Silver Training - Leadership"
    },
    {
      term: "Overcoming Obstacles (Obstakels Wegnemen)",
      definition: "Key QRM leadership responsibility: Identifying and removing environmental obstacles that prevent teams from achieving their goals.",
      category: "qrm-leadership",
      source: "QRM Silver Training - Leadership"
    },
    
    // Company-Wide Implementation (Silver Lesson 6)
    {
      term: "Company-Wide Implementation (Bedrijfsbrede Toepassing)",
      definition: "Applying QRM across all departments of the organization after successful pilot implementation. Requires systematic scaling and change management.",
      category: "company-wide",
      source: "QRM Silver Training - Company-Wide"
    },
    {
      term: "FTMS (Finished To Manufacturing Specifications)",
      definition: "A system where the final specifications for the product are fully defined up front to avoid delays during manufacturing.",
      category: "company-wide",
      source: "QRM Silver Training - Company-Wide"
    },
    {
      term: "HL/MRP (High-Level MRP Planning)",
      definition: "High-level material requirement planning used in conjunction with QRM for strategic planning and purchasing only, not detailed scheduling.",
      category: "company-wide",
      source: "QRM Silver Training - Company-Wide"
    },
    {
      term: "Time Slicing",
      definition: "A technique to manage scarce resources by allocating them in specific time slots, ensuring efficient resource utilization across QRM cells.",
      category: "company-wide",
      source: "QRM Silver Training - Company-Wide"
    },
    {
      term: "CONWIP (Constant Work In Process)",
      definition: "A pull system that limits the number of works in process (WIP) in the production system, preventing overproduction and maintaining flow.",
      category: "company-wide",
      source: "QRM Silver Training - Company-Wide"
    },
    
    // Change Management (Silver Lesson 7)
    {
      term: "Resistance Management (Weerstand)",
      definition: "Addressing employee resistance to QRM implementation by understanding root causes and providing support rather than just pushing change.",
      category: "change-management",
      source: "QRM Silver Training - Change Management"
    },
    {
      term: "Cultural Transformation",
      definition: "Creating a new organizational culture that supports QRM principles of time-based thinking, team autonomy, and continuous improvement.",
      category: "change-management",
      source: "QRM Silver Training - Change Management"
    },
    {
      term: "MCT-Map Communication Tool",
      definition: "A QRM-specific visual tool for communicating change plans and MCT improvement targets to teams and stakeholders.",
      category: "change-management",
      source: "QRM Silver Training - Change Management"
    },
    {
      term: "Removing Impediments (Remmende Krachten)",
      definition: "Change management strategy focusing on removing barriers rather than pushing drivers. More effective approach for QRM transformation.",
      category: "change-management",
      source: "QRM Silver Training - Change Management"
    },
    
    // Implementation Methodology (Silver Lesson 9)
    {
      term: "QRM Implementation Phases",
      definition: "5-phase approach: 1) Introduction, 2) Analysis, 3) Preparation, 4) Pilot, 5) Company-wide rollout. Each phase has specific deliverables and success criteria.",
      category: "implementation",
      source: "QRM Silver Training - Implementation"
    },
    {
      term: "Pain Points Analysis",
      definition: "Systematic identification of bottlenecks or problems within the production process during QRM analysis phase.",
      category: "implementation",
      source: "QRM Silver Training - Implementation"
    },
    {
      term: "Multi-Moment Analysis",
      definition: "Analysis of a process at several points in time to understand variability and identify improvement opportunities.",
      category: "implementation",
      source: "QRM Silver Training - Implementation"
    },
    {
      term: "Tagging",
      definition: "A technique for identifying and tracking specific issues in the process during QRM analysis and improvement phases.",
      category: "implementation",
      source: "QRM Silver Training - Implementation"
    },
    {
      term: "RASIC Matrix",
      definition: "Responsibility assignment matrix (Responsible, Accountable, Supported, Informed, Consulted) clarifying roles in QRM processes.",
      category: "implementation",
      source: "QRM Silver Training - Implementation"
    },
    {
      term: "Skill Matrix",
      definition: "A visual representation of employee skills and capabilities used for cross-training planning in QRM cells.",
      category: "implementation",
      source: "QRM Silver Training - Implementation"
    },
    {
      term: "Shop Floor Control",
      definition: "Management techniques to ensure production targets are met while maintaining QRM principles of flow and reduced lead times.",
      category: "implementation",
      source: "QRM Silver Training - Implementation"
    },
    {
      term: "WIP Limits",
      definition: "Constraints placed on the amount of unfinished work allowed within a process to maintain flow and prevent bottlenecks.",
      category: "implementation",
      source: "QRM Silver Training - Implementation"
    },
    
    // Advanced QRM Concepts
    {
      term: "FTMS (Focus Target Market Segment)",
      definition: "The specific customer segment or product family that a QRM cell focuses on. Critical for organizing QRM cells effectively.",
      category: "qrm-organization",
      source: "QRM Silver Training"
    },
    {
      term: "Response Time Spiral (Responstijd Spiraal)",
      definition: "A diagram illustrating how delays create a cycle of increasing lead times. Shows the compounding negative effects of poor time management.",
      category: "qrm-concepts",
      source: "QRM Silver Training"
    },
    {
      term: "QRM Number (QRM-getal)",
      definition: "A metric used to track progress and measure the effectiveness of QRM implementation. Key performance indicator for QRM success.",
      category: "qrm-metrics",
      source: "QRM Silver Training"
    },
    {
      term: "Suboptimization",
      definition: "A situation where individual parts of a system are optimized, but overall system performance is not optimized. QRM avoids this by focusing on the entire process.",
      category: "qrm-concepts",
      source: "QRM Silver Training"
    },
    
    // Process & Measurement Terms
    {
      term: "Touch Time",
      definition: "The actual processing time of an order, excluding waiting times. Used to identify value-added vs non-value-added time in MCT analysis.",
      category: "process-measurement",
      source: "QRM Silver Training"
    },
    {
      term: "Elapsed Time",
      definition: "The total time an order spends in the system, including waiting and processing times. Key metric for MCT analysis and improvement.",
      category: "process-measurement",
      source: "QRM Silver Training"
    },
    {
      term: "Grey Time (Non-Value-Added Time)",
      definition: "Time spent on activities that do not directly add value to the product (waiting, movement, etc.). Primary target for QRM reduction efforts through MCT optimization.",
      category: "process-measurement",
      source: "QRM Silver Training"
    },
    {
      term: "Critical Path Analysis",
      definition: "The identification of the longest sequence of tasks impacting total lead time (MCT) within the system. Essential for prioritizing improvement efforts.",
      category: "process-measurement",
      source: "QRM Silver Training"
    },
    
    // Advanced Mathematical Concepts
    {
      term: "Lead Time Formula",
      definition: "Doorlooptijd = V × M × JT + JT, where M = b/(1-b) and b is utilization rate. Shows exponential relationship between utilization and lead time.",
      category: "qrm-mathematics",
      source: "QRM Silver Training - System Dynamics"
    },
    {
      term: "M-Factor (Multiplication Factor)",
      definition: "M = b/(1-b) where b is utilization rate. Demonstrates how lead time increases exponentially with higher utilization in QRM systems.",
      category: "qrm-mathematics",
      source: "QRM Silver Training - System Dynamics"
    },
    
    // Lean Tools & VSM
    {
      term: "Value Stream Mapping (VSM)",
      definition: "A lean technique to analyze and design the flow of materials and information required to bring a product to the customer. Must have clear product families to be effective.",
      category: "lean-tools",
      source: "QRM Alpha & Silver Training"
    },
    {
      term: "Material and Information Flow (MIF)",
      definition: "Diagram visualizing process steps involved in manufacturing, showing both material flow and information flow for MCT analysis.",
      category: "vsm-concepts",
      source: "QRM Silver Training"
    },
    {
      term: "Gemba Walks",
      definition: "On-site observations to understand the actual process flow. Two rounds suggested: orientation and detailed data gathering. Essential for accurate VSM and MCT measurement.",
      category: "lean-tools",
      source: "QRM Alpha Training"
    },
    
    // Financial & Business Terms
    {
      term: "EBITDA",
      definition: "Earnings Before Interest, Taxes, Depreciation, and Amortization. Key financial metric for operational performance and QRM ROI analysis.",
      category: "financial",
      source: "Business Analysis"
    },
    {
      term: "Volume Discount (Volumekorting)",
      definition: "A discount offered by suppliers for purchasing goods in large quantities. A key strategy in QRM supply chain optimization.",
      category: "supply-chain",
      source: "QRM Silver Training"
    },
    {
      term: "Rush Orders (Spoedopdrachten)",
      definition: "Orders that require faster-than-usual turnaround time. QRM aims to reduce the need for and costs associated with rush orders through improved MCT.",
      category: "production-control",
      source: "QRM Silver Training"
    },
    
    // Implementation & Control
    {
      term: "Safety Time (Veiligheidstijd)",
      definition: "Buffer time added to schedules to account for potential delays. QRM strives to minimize this through improved reliability and reserve capacity.",
      category: "production-control",
      source: "QRM Silver Training"
    },
    {
      term: "Cross-Training",
      definition: "Training employees to perform multiple tasks or functions within the QRM cell. Provides flexibility and enhances team responsiveness through member interchangeability.",
      category: "implementation",
      source: "QRM Silver Training"
    },
    {
      term: "Ownership (Eigenaarschap)",
      definition: "Responsibility and accountability for work. Key principle in QRM where team members take ownership of their processes and results.",
      category: "qrm-philosophy",
      source: "QRM Silver Training"
    },
    {
      term: "Multi-functional Teams",
      definition: "QRM teams composed of individuals from different functional areas, enabling faster decision-making and problem-solving within cells.",
      category: "qrm-teams",
      source: "QRM Silver Training"
    },
    
    // Systems & Technology
    {
      term: "MRP/ERP Systems",
      definition: "Material Requirements Planning/Enterprise Resource Planning systems. Useful tools for QRM but not sufficient for successful implementation alone - teams and culture are essential.",
      category: "systems",
      source: "QRM Silver Training"
    },
    {
      term: "Tagging Systems",
      definition: "Physical or IT systems that help track the movement of products or orders through the manufacturing system for accurate MCT measurement.",
      category: "systems",
      source: "QRM Silver Training"
    },
    {
      term: "Kanban",
      definition: "A visual signaling system for production control that helps manage workflow and inventory levels in QRM implementation.",
      category: "lean-tools",
      source: "QRM Silver Training"
    },
    {
      term: "Visual Management",
      definition: "Use of visual aids (Kanban boards, flow charts, team boards) to make processes transparent and efficient in QRM cells.",
      category: "implementation",
      source: "QRM Silver Training"
    },
    
    // QRM GOLD EXPERT LEVEL - Advanced Concepts
    {
      term: "HOST Leadership Model",
      definition: "Advanced QRM Gold leadership framework with six roles: Initiator, Inviter, Space Creator, Gatekeeper, Connector, and Participator. Expert approach to creating environments where teams thrive.",
      category: "qrm-gold",
      source: "QRM Gold Training - Leadership"
    },
    {
      term: "Leader as Host (Gastheer/Gastvrouw)",
      definition: "QRM Gold leadership style focused on creating a context where individuals can thrive and contribute to success. Involves actively shaping environment and fostering positive interactions.",
      category: "qrm-gold",
      source: "QRM Gold Training - Leadership"
    },
    {
      term: "Step In/Step Back Leadership",
      definition: "Flexible QRM Gold leadership approach where the leader adjusts involvement based on situation needs. Step in for direction when needed, step back to empower teams.",
      category: "qrm-gold",
      source: "QRM Gold Training - Leadership"
    },
    {
      term: "Solution-Focused Work (Oplossingsgericht Werken)",
      definition: "QRM Gold methodology emphasizing future-oriented, strength-based, and progressive approaches to problem-solving. Contrasts with problem-focused approaches.",
      category: "qrm-gold",
      source: "QRM Gold Training - Solution-Oriented"
    },
    {
      term: "Solution Talk vs Problem Talk",
      definition: "QRM Gold communication principle: Focus on desired outcomes, strengths, and next steps rather than dwelling on negative aspects. Uses specific questioning techniques.",
      category: "qrm-gold",
      source: "QRM Gold Training - Solution-Oriented"
    },
    {
      term: "Three Principles of Solution-Focused Work",
      definition: "QRM Gold principles: 1) Don't fix what isn't broken, 2) Identify what works and do more of it, 3) If something doesn't work, try something else.",
      category: "qrm-gold",
      source: "QRM Gold Training - Solution-Oriented"
    },
    {
      term: "User's Guide to the Future",
      definition: "QRM Gold structured approach to solution-finding: Define change wish, visualize desired future, identify focus areas, track progress, plan next steps with visual timeline.",
      category: "qrm-gold",
      source: "QRM Gold Training - Solution-Oriented"
    },
    {
      term: "O.A.S.E. Framework",
      definition: "QRM Gold collaboration methodology: Opening, Affirmation, Suggestions, Evaluation. Framework for solution-focused team discussions and collaborative problem-solving.",
      category: "qrm-gold",
      source: "QRM Gold Training - Solution-Oriented"
    },
    {
      term: "Small Steps/Progressive Approach",
      definition: "QRM Gold methodology favoring manageable steps towards desired outcomes. Increases success likelihood and allows course correction, contrasts with large sweeping changes.",
      category: "qrm-gold",
      source: "QRM Gold Training - Solution-Oriented"
    },
    {
      term: "Strength-Based Approach",
      definition: "QRM Gold focus on resources and capabilities already present in team or system rather than solely on deficits or problems. Builds on existing successes.",
      category: "qrm-gold",
      source: "QRM Gold Training - Solution-Oriented"
    },
    {
      term: "Advanced System Dynamics (Geavanceerde Systeemdynamica)",
      definition: "QRM Gold expert-level understanding of complex system behaviors, feedback loops, and nonlinear relationships beyond Silver level system dynamics.",
      category: "qrm-gold",
      source: "QRM Gold Training - Advanced"
    },
    {
      term: "Company-Wide QRM Application (Bedrijfsbrede Toepassing)",
      definition: "QRM Gold expert methodology for scaling QRM across entire organizations. Involves advanced change management, leadership transformation, and system integration.",
      category: "qrm-gold",
      source: "QRM Gold Training - Implementation"
    },
    
    // QRM GOLD FINANCE - Expert Financial Methodology
    {
      term: "QRM Contribution Margin Analysis",
      definition: "QRM Gold financial methodology focusing on true profitability rather than traditional cost allocation. EBITDA approximates contribution margin for investment decisions.",
      category: "qrm-finance",
      source: "QRM Gold Finance Training"
    },
    {
      term: "QRM Revenue Impact Formula",
      definition: "QRM Gold principle: Revenue_Growth = MCT_Reduction × 0.2. From exercises: 50% MCT reduction = 10% sales increase, 40% MCT reduction = 15% sales increase.",
      category: "qrm-finance",
      source: "QRM Gold Finance Training"
    },
    {
      term: "Working Capital Management through WIP",
      definition: "QRM Gold financial strategy: WIP reduction provides immediate cash flow benefit. From Exercise 3: €250,000 WIP reduction with 15% carrying cost savings.",
      category: "qrm-finance",
      source: "QRM Gold Finance Training"
    },
    {
      term: "Cost-Volume-Profit (CVP) Analysis",
      definition: "QRM Gold financial analysis technique for evaluating relationship between costs, volume, and profit in QRM investment decisions.",
      category: "qrm-finance",
      source: "QRM Gold Finance Training"
    },
    {
      term: "White Space Costs",
      definition: "QRM Gold concept: Hidden costs of idle capacity often neglected in traditional accounting. Critical for accurate QRM investment analysis.",
      category: "qrm-finance",
      source: "QRM Gold Finance Training"
    },
    {
      term: "Open Book Management",
      definition: "QRM Gold financial transparency approach involving greater delegation of financial responsibilities to QRM teams within the organization.",
      category: "qrm-finance",
      source: "QRM Gold Finance Training"
    },
    {
      term: "Multi-Variable ROI Analysis",
      definition: "QRM Gold advanced technique analyzing ROI across multiple interdependent variables: pricing, capacity, inventory, and lead time simultaneously.",
      category: "qrm-finance",
      source: "QRM Gold Finance Training"
    },
    {
      term: "QRM Investment Scenarios",
      definition: "QRM Gold approach: Supplier changes (30% cost increase for 40% lead time reduction), capacity investments (€350K machine for bottleneck removal), inventory optimization.",
      category: "qrm-finance",
      source: "QRM Gold Finance Training"
    },
    
    // QRM GOLD ADVANCED CONCEPTS - Buffering & Strategic Planning
    {
      term: "QRM Buffering Strategies",
      definition: "QRM Gold advanced approach: Inventory buffers (25% annual cost), time buffers (lead time reduction), capacity buffers (reserve capacity for variability). Strategic buffering for high-mix environments.",
      category: "qrm-buffering",
      source: "QRM Gold Training - Buffering"
    },
    {
      term: "Dealer Buffer Strategy",
      definition: "QRM Gold distribution approach: Using dealers as buffers to store finished goods, reducing immediate production capacity needs and managing demand fluctuations.",
      category: "qrm-buffering",
      source: "QRM Gold Training - Buffering"
    },
    {
      term: "Finished Goods Inventory Buffer",
      definition: "QRM Gold strategy: Producing standard products in advance with 25% annual inventory carrying cost. Classic inventory buffering for expected demand management.",
      category: "qrm-buffering",
      source: "QRM Gold Training - Buffering"
    },
    {
      term: "Capacity Buffer Management",
      definition: "QRM Gold principle: Using remaining capacity for unexpected demand or production disruptions. Strategic capacity planning beyond base requirements.",
      category: "qrm-buffering",
      source: "QRM Gold Training - Buffering"
    },
    {
      term: "S&OP (Sales & Operations Planning)",
      definition: "QRM Gold strategic process: Input gathering, demand review, supply review, pre-S&OP meeting, executive S&OP meeting. Aligns demand and supply with rolling forecasts.",
      category: "qrm-strategic",
      source: "QRM Gold Training - Strategic Planning"
    },
    {
      term: "Reserve Capacity Strategy",
      definition: "QRM Gold strategic policy: Building flexible time buffers and improvement time to prepare for fluctuations. Strategic preparation for variability management.",
      category: "qrm-strategic",
      source: "QRM Gold Training - Strategic Planning"
    },
    {
      term: "Mixed-Model Levelling",
      definition: "QRM Gold tactical approach to reduce variability in demand and production. Strategic implications for long-term production planning with mixed models.",
      category: "qrm-strategic",
      source: "QRM Gold Training - Strategic Planning"
    },
    {
      term: "Time Slicing",
      definition: "QRM Gold technique for managing scarce resources by scheduling them across time slots. Strategic resource allocation methodology.",
      category: "qrm-strategic",
      source: "QRM Gold Training - Strategic Planning"
    },
    {
      term: "KOOP (Customer Order Decoupling Point)",
      definition: "QRM Gold strategic concept: Point in value stream where customer orders are decoupled from production planning. Critical for QRM strategic design.",
      category: "qrm-strategic",
      source: "QRM Gold Training - Strategic Planning"
    },
    {
      term: "Time Fence Planning",
      definition: "QRM Gold planning methodology: Establishing time boundaries for different types of planning decisions and changes in production schedules.",
      category: "qrm-strategic",
      source: "QRM Gold Training - Strategic Planning"
    },
    {
      term: "Capacity Flexibility Strategy",
      definition: "QRM Gold approach: Making existing capacity more flexible rather than just adding fixed reserve capacity. Strategic flexibility over fixed resources.",
      category: "qrm-strategic",
      source: "QRM Gold Training - Strategic Planning"
    },
    {
      term: "Visual Management Flags",
      definition: "QRM Gold tool: Using flags for capacity control as visual management technique. Advanced visual control systems for capacity management.",
      category: "qrm-tools",
      source: "QRM Gold Training - Strategic Planning"
    },
    
    // QRM GOLD ADVANCED SYSTEM DYNAMICS & POWER OF SIX
    {
      term: "Power of Six Rule (Tubino's Formula)",
      definition: "Advanced QRM Gold mathematical relationship: (Cost_after/Cost_before)^6 = Lead_time_after/Lead_before. Expert formula for cost reduction through lead time optimization.",
      category: "qrm-advanced",
      source: "QRM Gold Training - Power of Six Tubino"
    },
    {
      term: "QRM Mathematical Model: (M/M₀) = (L/L₀)^k",
      definition: "Sophisticated QRM Gold formula where M is performance metric, L is lead time, k ≈ 0.17 for cost. Empirically determined from QRM studies.",
      category: "qrm-advanced",
      source: "QRM Gold Training - Power of Six Tubino"
    },
    {
      term: "Proportional Behavior Property",
      definition: "QRM Gold principle: Improvements in performance measure (as proportion of original) are function only of proportional improvement in lead time.",
      category: "qrm-advanced",
      source: "QRM Gold Training - Power of Six Tubino"
    },
    {
      term: "Transitive Consistency Property",
      definition: "QRM Gold mathematical principle: If lead time reduction A→B improves metric by X, and B→C by Y, then A→C improves by X+Y.",
      category: "qrm-advanced",
      source: "QRM Gold Training - Power of Six Tubino"
    },
    {
      term: "Kingman's Equation",
      definition: "Advanced QRM Gold system dynamics formula: Flow time = (Ca² + Cp²) / (2(1 - u)) * te + te. Models impact of variability and utilization on flow time.",
      category: "qrm-advanced",
      source: "QRM Gold Training - Advanced System Dynamics"
    },
    {
      term: "Variability Coefficients (Ca², Cp²)",
      definition: "QRM Gold system dynamics parameters: Ca² = arrival variability coefficient, Cp² = processing variability coefficient. Critical for advanced flow time calculations.",
      category: "qrm-advanced",
      source: "QRM Gold Training - Advanced System Dynamics"
    },
    {
      term: "Pooling Effect",
      definition: "QRM Gold concept: Benefits of multiple parallel resources in reducing impact of variability. Advanced capacity planning principle.",
      category: "qrm-advanced",
      source: "QRM Gold Training - Advanced System Dynamics"
    },
    {
      term: "DD-MRP (Demand Driven MRP)",
      definition: "QRM Gold tactical planning improvement over traditional MRP: Strategic decoupling, buffer profiles, dynamic adjustments, demand-driven planning, visible collaborative execution.",
      category: "qrm-tactical",
      source: "QRM Gold Training - Tactical Planning"
    },
    {
      term: "Strategic Decoupling",
      definition: "QRM Gold DD-MRP concept: Creating buffers to absorb variability and decouple production planning from demand fluctuations.",
      category: "qrm-tactical",
      source: "QRM Gold Training - Tactical Planning"
    },
    {
      term: "Buffer Profiles and Levels",
      definition: "QRM Gold tactical tool: Visualizing buffer levels for better control in DD-MRP systems. Dynamic buffer management approach.",
      category: "qrm-tactical",
      source: "QRM Gold Training - Tactical Planning"
    },
    {
      term: "Load Limit (Capaciteitslimiet)",
      definition: "QRM Gold tactical constraint: Setting capacity usage limits around 80% to create safety margin and reduce system nervousness.",
      category: "qrm-tactical",
      source: "QRM Gold Training - Tactical Planning"
    },
    {
      term: "MRP Nervousness",
      definition: "QRM Gold problem: Instability in production plans caused by small changes in forecasts or inputs. DD-MRP reduces this through buffer management.",
      category: "qrm-tactical",
      source: "QRM Gold Training - Tactical Planning"
    },
    {
      term: "Ripple Effect",
      definition: "QRM Gold tactical concern: Cascading effect of changes in one part of the plan on other parts. Managed through flattened BOM and reduced routing steps.",
      category: "qrm-tactical",
      source: "QRM Gold Training - Tactical Planning"
    },
    {
      term: "POLCA Quantum",
      definition: "QRM Gold operational concept: The amount of work represented by one POLCA card. Critical for sizing POLCA loops and managing flow.",
      category: "qrm-operational",
      source: "QRM Gold Training - Operational Planning"
    },
    {
      term: "Dispatch List",
      definition: "QRM Gold operational tool: List of authorized tasks ready to be worked on in QRM cells. Used with POLCA for flow control.",
      category: "qrm-operational",
      source: "QRM Gold Training - Operational Planning"
    },
    {
      term: "Sequencing Rules",
      definition: "QRM Gold operational methodology: Using FIFO, SPT, EDD, Critical Ratio, Slack rules to prioritize tasks and optimize flow time, lateness, and tardiness.",
      category: "qrm-operational",
      source: "QRM Gold Training - Operational Planning"
    },
    {
      term: "Stand-up Meetings",
      definition: "QRM Gold operational practice: Short, frequent meetings to address daily issues, synchronize work, identify problems, and promote teamwork in QRM cells.",
      category: "qrm-operational",
      source: "QRM Gold Training - Operational Planning"
    },
    
    // AUTHENTIC MCT TOOLS - Real QRM Implementation Tools
    {
      term: "MCT Tool (Excel-based)",
      definition: "Authentic QRM calculation tool for visual MCT mapping with critical path identification. Uses sequence and parallel numbering for hierarchical process representation.",
      category: "mct-tools",
      source: "QRM MCT Tool Tutorial"
    },
    {
      term: "MCT Map Generation",
      definition: "Visual representation process showing critical path through manufacturing sequences. Tool generates automatic MCT mapping from input data for analysis.",
      category: "mct-tools",
      source: "QRM MCT Tool Tutorial"
    },
    {
      term: "Critical Path Identification",
      definition: "Authentic QRM methodology: Identifying the longest path in MCT map representing overall cycle time. Visual identification of bottlenecks and improvement areas.",
      category: "mct-tools",
      source: "QRM MCT Tool Tutorial"
    },
    {
      term: "Touch Time vs Non-Touch Time Analysis",
      definition: "MCT Tool methodology: Touch time = actively working on unit, Non-touch time = waiting/queues/inventory. Primary focus for MCT reduction efforts.",
      category: "mct-tools",
      source: "QRM MCT Tool Tutorial"
    },
    {
      term: "Process Routings",
      definition: "Detailed sequence of operations for manufacturing products. Essential input for MCT Tool analysis and critical path calculation.",
      category: "mct-tools",
      source: "QRM MCT Tool Tutorial"
    },
    {
      term: "Sequence and Parallel Numbering",
      definition: "MCT Tool methodology: Hierarchical representation using sequence numbers and parallel numbers for converging and diverging process flows.",
      category: "mct-tools",
      source: "QRM MCT Tool Tutorial"
    },
    {
      term: "Converging and Diverging Nodes",
      definition: "MCT Tool concepts: Points where multiple processes converge (merge) or diverge (split) in manufacturing flow. Critical for accurate MCT mapping.",
      category: "mct-tools",
      source: "QRM MCT Tool Tutorial"
    },
    {
      term: "Inventory Impact Integration",
      definition: "MCT Tool approach: Integration of WIP, intermediate stock, raw materials, and finished goods levels into MCT calculation for realistic analysis.",
      category: "mct-tools",
      source: "QRM MCT Tool Tutorial"
    },
    {
      term: "Shift Pattern Analysis",
      definition: "MCT Tool data: Operating days/week, hours/day for each step. Essential for accurate calendar time calculation in MCT analysis.",
      category: "mct-tools",
      source: "QRM MCT Tool Tutorial"
    },
    {
      term: "Visual MCT Improvement Identification",
      definition: "MCT Tool benefit: Visual highlighting of long durations, bottlenecks, and improvement opportunities on generated MCT map for prioritization.",
      category: "mct-tools",
      source: "QRM MCT Tool Tutorial"
    },
    
    // RAJAN SURI'S AUTHENTIC QRM - "It's About Time" Definitive Concepts
    {
      term: "Rajan Suri's MCT Definition (Authoritative)",
      definition: "Manufacturing Critical-path Time: The typical amount of calendar time from when a customer creates an order, through the critical path, until the first piece of that order is delivered to the customer.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix A"
    },
    {
      term: "Rajan Suri's Flow Time Formula",
      definition: "Authentic QRM creator's formula: Flow Time = (AV × M × TJ) + TJ, where AV = Average Variability, M = u/(1-u), TJ = Average Job Time.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix D"
    },
    {
      term: "Magnifying Effect of Utilization (M-Factor)",
      definition: "Rajan Suri's formula: M = u/(1-u) where u is utilization rate. Shows how utilization amplifies variability's impact on flow time.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix D"
    },
    {
      term: "Average Variability (AV)",
      definition: "Rajan Suri's concept: Incorporates both arrival and job time variability. Critical component requiring data analysis for accurate flow time calculations.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix D"
    },
    {
      term: "First Piece Focus",
      definition: "Rajan Suri's QRM principle: Focus on time to deliver the first piece of an order, not the entire batch. Avoids inventory management complexities.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix A"
    },
    {
      term: "System-Wide Waste Reduction",
      definition: "Rajan Suri's approach: Eliminate waste across the entire system, not just individual operations. Traditional lead time metrics often fail to capture this.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix A"
    },
    {
      term: "Time-Based Approach vs Cost-Based",
      definition: "Rajan Suri's core QRM philosophy: Emphasize time-based optimization rather than cost-based approach to improve efficiency and competitive advantage.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix A"
    },
    {
      term: "High-Level Estimates Sufficient",
      definition: "Rajan Suri's implementation guidance: In initial QRM stages, rough MCT estimates are enough to highlight major improvement opportunities.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix A"
    },
    {
      term: "Critical Items Focus",
      definition: "Rajan Suri's principle: Focus on critical components or processes in the system. Non-critical components should be excluded from initial MCT calculations.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix A"
    },
    {
      term: "FTMS (Focused Target Market Segment)",
      definition: "Rajan Suri's strategic concept: Market segments defined by job characteristics like customer/market, demand volume, order size, pricing, profitability, lead times, customization.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix B"
    },
    {
      term: "QRM Cell Design Philosophy",
      definition: "Rajan Suri's approach: Don't simply replicate existing processes in smaller space - fundamentally redesign operations to minimize MCT through creative solutions.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix C"
    },
    {
      term: "Time-Slicing for Shared Resources",
      definition: "Rajan Suri's strategy: Allocating specific time slots to QRM cells for shared resources (machines, personnel), ensuring predictable access and process ownership.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix C"
    },
    
    // RAJAN SURI'S POLCA SYSTEM - Final Appendix E (Complete QRM)
    {
      term: "POLCA Card Calculation Formula",
      definition: "Rajan Suri's authentic formula: NAB = (LA + LB) × (FAB / D), where NAB = Number of A/B POLCA cards, LA/LB = Lead times, FAB = Cards flowing A to B, D = Workdays in planning period.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix E"
    },
    {
      term: "POLCA Quantum Determination",
      definition: "Rajan Suri's approach: Carefully decide quantum (work amount per POLCA card), balancing frequent signals with minimal material handling. Consider job splitting or card waiting.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix E"
    },
    {
      term: "POLCA as Control System",
      definition: "Rajan Suri's principle: POLCA executes a plan but doesn't improve poor planning. Must have cellular structure and proper capacity planning first.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix E"
    },
    {
      term: "HL/MRP (High-Level MRP)",
      definition: "Rajan Suri's system modification: Modify existing MRP to function as High-Level MRP, producing dispatch lists sequenced by release authorization times for POLCA.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix E"
    },
    {
      term: "POLCA Champion Role",
      definition: "Rajan Suri's implementation strategy: Designate a POLCA champion for ongoing support, issue resolution, and continuous improvement of the POLCA system.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix E"
    },
    {
      term: "Rough-Cut Capacity Planning",
      definition: "Rajan Suri's requirement: Ensure sufficient spare capacity within all QRM cells to prevent bottlenecks and ensure POLCA system functions correctly.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix E"
    },
    {
      term: "POLCA Card Design Principles",
      definition: "Rajan Suri's specifications: Color-coding cells, unique serial numbers, clear labeling for visual management and tracking. Critical for effective POLCA operation.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix E"
    },
    {
      term: "Incremental POLCA Implementation",
      definition: "Rajan Suri's strategy: Start POLCA in limited area and expand over time, rather than attempting company-wide rollout simultaneously.",
      category: "rajan-suri",
      source: "Rajan Suri - It's About Time, Appendix E"
    },
    
    // REAL-WORLD QRM SUCCESS CASES - Industry Validation
    {
      term: "NIKO Case Study Results",
      definition: "Electrical components manufacturer: 87% lead time reduction (203 → 26 hours) over 3 years (2015-2017). Progressive improvement: 28% → 58% → 58% reduction per phase.",
      category: "case-studies",
      source: "NIKO QRM Implementation Case Study"
    },
    {
      term: "Perkins Industrial Cabinet Results",
      definition: "Complex manufacturing case: Realistic 30% lead time reduction achieved. Lesson: Not all processes suit dramatic QRM improvements - focus on non-value-added time.",
      category: "case-studies",
      source: "Perkins QRM Case Study"
    },
    {
      term: "Belgian Manufacturer Results",
      definition: "Metal manufacturing company achieved significant revenue growth, lead time reduction, and improved EBITDA margins through QRM implementation.",
      category: "case-studies",
      source: "QRM Success Story"
    },
    {
      term: "Metal Supply VL Challenges",
      definition: "Case study showing QRM implementation challenges: Uncontrolled FIFO flow, 95% capacity utilization, poor material management. Demonstrates need for systematic QRM approach.",
      category: "case-studies",
      source: "Metal Supply VL QRM Case Study"
    },
    {
      term: "QRM Implementation Timeline Validation",
      definition: "Real cases show 3-year progressive improvement pattern: NIKO (2015-2017), Belgian manufacturers (multi-year), validating 30% → 70% → 100% benefit realization model.",
      category: "case-studies",
      source: "Multiple QRM Case Studies"
    },
    {
      term: "Industry-Specific QRM Results",
      definition: "QRM success varies by industry: Electrical components (significant reduction), Metal manufacturing (strong revenue growth), Complex processes (realistic improvements achievable).",
      category: "case-studies",
      source: "Multiple QRM Case Studies"
    },
    
    // AGRICULTURE & FOOD SECTOR QRM APPLICATION
    {
      term: "John Deere QRM Success",
      definition: "Agricultural machinery: 78% lead time reduction with ~200 suppliers, $890M inventory reduction, $107M cost savings. QRM for seasonal demand management (2/3 sales in spring).",
      category: "agriculture-food",
      source: "John Deere QRM Case Study - Agriculture Analysis"
    },
    {
      term: "Nestlé Local Quick Response Strategy",
      definition: "Food processing research: Local just-in-time production vs global sourcing. Quick response often more profitable due to lower safety stock requirements with uncertain demand.",
      category: "agriculture-food",
      source: "Nestlé QRM Research - Food Industry Analysis"
    },
    {
      term: "Packo Inox Hybrid Model",
      definition: "Food industry supplier: Lean for standardized production lines, QRM methods for high-mix components. Demonstrates optimal strategy combination in food processing.",
      category: "agriculture-food",
      source: "Packo Inox Case Study - Food Industry Analysis"
    },
    {
      term: "QRM Suitability by Food Sector",
      definition: "High suitability: High-mix food processing, specialty products, seasonal campaigns. Low suitability: Primary agriculture (nature-controlled), mass production (Lean better).",
      category: "agriculture-food",
      source: "Agriculture & Food Sector Analysis"
    },
    {
      term: "Perishability Factor in QRM",
      definition: "Time criticality in food: Fresh dairy, meat, bakery, cut vegetables have limited shelf life. QRM's time focus highly relevant for fresh product supply chains.",
      category: "agriculture-food",
      source: "Agriculture & Food Sector Analysis"
    },
    {
      term: "Seasonal Production Challenges",
      definition: "Agriculture limitation: Crop growth cycles, harvest timing, seasonal labor needs. QRM difficult to apply to nature-controlled processes but valuable in support processes.",
      category: "agriculture-food",
      source: "Agriculture & Food Sector Analysis"
    },
    {
      term: "Food Safety vs Lead Time Balance",
      definition: "Food industry challenge: HACCP requirements, cleaning protocols, traceability add process steps. QRM must integrate safety without compromising speed.",
      category: "agriculture-food",
      source: "Agriculture & Food Sector Analysis"
    },
    {
      term: "Just-in-Time Fresh Distribution",
      definition: "Food sector QRM application: Daily fresh delivery, cross-docking, pull systems based on retail sales. 24-hour farm-to-shelf for fresh products.",
      category: "agriculture-food",
      source: "Agriculture & Food Sector Analysis"
    },
    {
      term: "Hybrid Strategy Recommendation",
      definition: "Best practice for food: Lean for standardized processes + QRM for high-mix segments + Agile for market responsiveness. No single strategy dominates.",
      category: "agriculture-food",
      source: "Agriculture & Food Sector Analysis"
    },
    
    // ADVANCED AGRICULTURE & FOOD QRM INTELLIGENCE (Comprehensive Sector Analysis)
    {
      term: "24-Hour Fresh Food Chain",
      definition: "Time-critical agriculture example: Strawberries picked morning, cooled within hour, distributed same afternoon, in supermarket next morning. Natural 'quick response' driven by perishability.",
      category: "agriculture-food-advanced",
      source: "QRM Agriculture & Food Sector Comprehensive Analysis"
    },
    {
      term: "BelOrta Day Trading System",
      definition: "Belgian fruit cooperative: Fruit auctioned in morning and picked up afternoon, shortening chain by eliminating intermediate steps. QRM principle applied to agricultural auctions.",
      category: "agriculture-food-advanced",
      source: "QRM Agriculture & Food Sector Comprehensive Analysis"
    },
    {
      term: "CARTAMA Lean Agriculture Model",
      definition: "Colombian avocado producer: Used Lean to control costs and waste per hectare. Focus on process improvement and worker capacity development before buying more machines.",
      category: "agriculture-food-advanced",
      source: "QRM Agriculture & Food Sector Comprehensive Analysis"
    },
    {
      term: "Food Processing SMED Success",
      definition: "Salsa sauce producer: Reduced changeover time from 8 hours to 1 hour through SMED, enabling smaller batches and shorter lead times for specific flavors.",
      category: "agriculture-food-advanced",
      source: "QRM Agriculture & Food Sector Comprehensive Analysis"
    },
    {
      term: "COVID Food Industry Agility",
      definition: "Pandemic response: Dairy factories switched from foodservice cheese to consumer packaging. Breweries switched to sanitizing alcohol. Ultimate form of supply chain agility.",
      category: "agriculture-food-advanced",
      source: "QRM Agriculture & Food Sector Comprehensive Analysis"
    },
    {
      term: "Seasonal Demand Management (Agriculture)",
      definition: "John Deere challenge: 2/3 of lawnmowers sold in spring. QRM solution: Supply chain with ~200 suppliers, 78% lead time reduction, just-in-time production instead of year-long inventory.",
      category: "agriculture-food-advanced",
      source: "QRM Agriculture & Food Sector Comprehensive Analysis"
    },
    {
      term: "ULO Storage as Market Buffer",
      definition: "Fruit growing strategy: Hard fruit (apples, pears) stored under ULO conditions for months, allowing growers to buffer market timing. Expensive but necessary for price optimization.",
      category: "agriculture-food-advanced",
      source: "QRM Agriculture & Food Sector Comprehensive Analysis"
    },
    {
      term: "Cross-Docking Fresh Distribution",
      definition: "Supermarket JIT strategy: Products arrive just-in-time at distribution center and immediately go out to stores without long storage. Critical for fresh product quality.",
      category: "agriculture-food-advanced",
      source: "QRM Agriculture & Food Sector Comprehensive Analysis"
    },
    {
      term: "Agile Crop Switching",
      definition: "Agricultural agility example: Farmer sees grain prices collapse in spring, decides last-minute to plant alternative crop (soy, rapeseed) for better returns. Strategic agility in farming.",
      category: "agriculture-food-advanced",
      source: "QRM Agriculture & Food Sector Comprehensive Analysis"
    },
    {
      term: "Bake-Off Oven Technology",
      definition: "Hybrid Lean+Agile: Dough prepared in factory, baked on-site in supermarket. Combines lean (transport unbaked product costs less) with agile (produce exactly when customer wants).",
      category: "agriculture-food-advanced",
      source: "QRM Agriculture & Food Sector Comprehensive Analysis"
    },
    
    // METALWORKING-SPECIFIC QRM (Expert Industry Knowledge)
    {
      term: "Gray Zone vs White Zone Time",
      definition: "Metalworking reality: 90-95% Gray Zone (waiting, transport, queues) vs 5-10% White Zone (actual cutting, welding, bending). QRM targets Gray Zone reduction.",
      category: "metalworking",
      source: "QRM for Metalworking Companies - Expert Guide"
    },
    {
      term: "Lead Time Spiral",
      definition: "Metalworking trap: High utilization → queues → long lead times → rush orders → schedule chaos → more utilization pressure. QRM breaks this spiral with 70-80% utilization target.",
      category: "metalworking",
      source: "QRM for Metalworking Companies - Expert Guide"
    },
    {
      term: "Sheet Metal Fabrication Cell",
      definition: "QRM cell example: Laser cutting + press brake + hardware insertion + welding stations co-located with cross-trained team handling electrical cabinet parts A-Z.",
      category: "metalworking",
      source: "QRM for Metalworking Companies - Expert Guide"
    },
    {
      term: "Q-ROC Applications in Metalworking",
      definition: "Quote-to-Order Q-ROC (inquiry to order entry), Engineering Q-ROC (CAD/CAM programming), Material Planning Q-ROC (steel/hardware procurement coordination).",
      category: "metalworking",
      source: "QRM for Metalworking Companies - Expert Guide"
    },
    {
      term: "Time Slicing for Metal Shops",
      definition: "Schedule by material type: Monday AM stainless steel, PM aluminum, Tuesday large mild steel. Reduces changeovers, improves nesting, creates predictability.",
      category: "metalworking",
      source: "QRM for Metalworking Companies - Expert Guide"
    },
    {
      term: "Cell Cycle Index (CCI)",
      definition: "Metalworking QRM metric: CCI = actual lead time in cell / processing time in cell. Target CCI of 3-4 (vs typical 10-20). Lower = less waiting relative to work.",
      category: "metalworking",
      source: "QRM for Metalworking Companies - Expert Guide"
    },
    {
      term: "Capacity Reservation Strategy",
      definition: "Metalworking approach: 70% standard work, 20% unplanned important work, 10% contingency buffer. Avoid 100% scheduling to maintain responsiveness.",
      category: "metalworking",
      source: "QRM for Metalworking Companies - Expert Guide"
    },
    {
      term: "FTMS for Metalworking",
      definition: "Focused Target Market Segments: Prototype Cell (ultra-fast), Standard Products (efficiency), Custom Engineered (project management). Each optimized for market needs.",
      category: "metalworking",
      source: "QRM for Metalworking Companies - Expert Guide"
    },
    {
      term: "Digital POLCA for Metal Shops",
      definition: "Software-based POLCA: Real-time cell capacity displays, virtual cards, automatic WIP tracking, integration with MES. Alternative to physical cards for advanced shops.",
      category: "metalworking",
      source: "QRM for Metalworking Companies - Expert Guide"
    },
    
    // QRM INSTITUTE OFFICIAL CONTENT - Planning Authority
    {
      term: "QRM Institute Planning Philosophy",
      definition: "Official QRM approach: Plan less, focus on managing demand and production variability. Aim for simplicity and aggregation over complex detailed planning.",
      category: "qrm-institute",
      source: "QRM Institute Webinar - Planning Problems 2021"
    },
    {
      term: "Assembly Line Model of Planning",
      definition: "QRM Institute methodology: Focus on managing WIP to maintain short, stable production lead time. Simplifies delivery date setting through WIP and order pool control.",
      category: "qrm-institute",
      source: "QRM Institute Webinar - Planning Problems 2021"
    },
    {
      term: "Order Pool Management",
      definition: "QRM Institute technique: Managing order queue with KPIs like release lateness and workload. Open time slots provide capacity for rush orders and unforeseen events.",
      category: "qrm-institute",
      source: "QRM Institute Webinar - Planning Problems 2021"
    },
    {
      term: "Capacity Management with Flags",
      definition: "QRM Institute visual system: Colored flags signal overtime and backlog size, providing clear visual feedback for team to react to capacity issues.",
      category: "qrm-institute",
      source: "QRM Institute Webinar - Planning Problems 2021"
    },
    {
      term: "Shop Floor Self-Regulation",
      definition: "QRM Institute principle: Strengthen self-regulating power and ownership on shop floor through visual boards and POLCA for enhanced responsiveness.",
      category: "qrm-institute",
      source: "QRM Institute Webinar - Planning Problems 2021"
    },
    {
      term: "Digital POLCA Support",
      definition: "QRM Institute advancement: Digital support systems for POLCA implementation in complex, high-mix, low-volume production environments.",
      category: "qrm-institute",
      source: "QRM Institute Webinar - Planning Problems 2021"
    },
    {
      term: "QRM Lead Time Target: 3-4 Days",
      definition: "QRM Institute benchmark: Reduction from traditional 3-4 weeks to QRM target of 3-4 days lead time. 90%+ lead time reduction achievable.",
      category: "qrm-institute",
      source: "QRM Institute Webinar - Planning Problems 2021"
    },
    {
      term: "Variability Management Priority",
      definition: "QRM Institute core focus: Recognizing and managing variability in demand and production as central to planning success. Variability is the enemy of planning.",
      category: "qrm-institute",
      source: "QRM Institute Webinar - Planning Problems 2021"
    },
    {
      term: "APS Limitations Recognition",
      definition: "QRM Institute wisdom: Advanced Planning Systems complexity and 'garbage in, garbage out' problems. Sophisticated tools less useful with uncertain data.",
      category: "qrm-institute",
      source: "QRM Institute Webinar - Planning Problems 2021"
    },
    {
      term: "Uncertainty Principle in Planning",
      definition: "QRM Institute concept: Uncertainty makes sophisticated planning tools less effective. Focus on simplicity and robust systems instead of complex algorithms.",
      category: "qrm-institute",
      source: "QRM Institute Webinar - Planning Problems 2021"
    },
    
    // BANI/TUNA FRAMEWORK - Modern Manufacturing Reality
    {
      term: "BANI Framework",
      definition: "Modern alternative to VUCA (Volatile, Uncertain, Complex, Ambiguous): Brittle, Anxious, Nonlinear, Incomprehensible. Describes current manufacturing environment requiring QRM responses.",
      category: "modern-frameworks",
      source: "BANI/TUNA Framework for Manufacturing"
    },
    {
      term: "Brittle (BANI)",
      definition: "Systems with single points of failure that cause complete collapse when one element fails. QRM response: Build Q-ROC cells with local buffers to eliminate dependencies.",
      category: "modern-frameworks", 
      source: "BANI/TUNA Framework for Manufacturing"
    },
    {
      term: "Anxious (BANI)",
      definition: "Constant priority shifts and reactive decision-making. QRM response: Install POLCA cards and visible work queues to prioritize by time, not urgency.",
      category: "modern-frameworks",
      source: "BANI/TUNA Framework for Manufacturing"
    },
    {
      term: "Nonlinear (BANI)",
      definition: "Small errors causing disproportionately large system impacts. QRM response: Daily feedback loops and cell team ownership for rapid problem detection and resolution.",
      category: "modern-frameworks",
      source: "BANI/TUNA Framework for Manufacturing"
    },
    {
      term: "Incomprehensible (BANI)",
      definition: "Too many metrics creating confusion about priorities. QRM response: Track only lead time as the north star metric, eliminate KPI overload.",
      category: "modern-frameworks",
      source: "BANI/TUNA Framework for Manufacturing"
    },
    {
      term: "TUNA Framework",
      definition: "Complementary to BANI: Turbulent, Uncertain, Novel, Ambiguous. Describes external pressures on manufacturing requiring adaptive QRM strategies.",
      category: "modern-frameworks",
      source: "BANI/TUNA Framework for Manufacturing"
    },
    {
      term: "Turbulent (TUNA)",
      definition: "Frequent external changes disrupting production flow. QRM response: Short decision lines inside cells to enable rapid re-sequencing and adaptation.",
      category: "modern-frameworks",
      source: "BANI/TUNA Framework for Manufacturing"
    },
    {
      term: "Uncertain (TUNA)",
      definition: "Unpredictable variables (lead times, staffing, costs) affecting operations. QRM response: Plan on time, not capacity, with WIP caps per step for robust flow.",
      category: "modern-frameworks",
      source: "BANI/TUNA Framework for Manufacturing"
    },
    {
      term: "Novel (TUNA)",
      definition: "New product requests or unprecedented customer demands. QRM response: Multi-skilled cells with tiny batches to learn fast and contain WIP during exploration.",
      category: "modern-frameworks",
      source: "BANI/TUNA Framework for Manufacturing"
    },
    // VUCA FRAMEWORK - Traditional Business Framework
    {
      term: "VUCA Framework",
      definition: "Traditional business framework: Volatile, Uncertain, Complex, Ambiguous. Older model for describing business environment, now enhanced by BANI/TUNA for manufacturing reality.",
      category: "modern-frameworks",
      source: "VUCA Framework - Traditional Business Model"
    },
    {
      term: "Volatile (VUCA)",
      definition: "Rapid and unpredictable changes in business environment. QRM response: Flexible capacity management and cross-trained teams to absorb variability.",
      category: "modern-frameworks",
      source: "VUCA Framework - Traditional Business Model"
    },
    {
      term: "Uncertain (VUCA)",
      definition: "Lack of predictability about future events and outcomes. QRM response: Focus on lead time reduction rather than demand forecasting accuracy.",
      category: "modern-frameworks",
      source: "VUCA Framework - Traditional Business Model"
    },
    {
      term: "Complex (VUCA)",
      definition: "Multiple interconnected factors making cause-and-effect difficult to determine. QRM response: Simplify through cell-based organization and single metric focus (lead time).",
      category: "modern-frameworks",
      source: "VUCA Framework - Traditional Business Model"
    },
    {
      term: "Ambiguous (VUCA)",
      definition: "Unclear relationships and meanings creating confusion. QRM response: Clear roles within Q-ROC cells and transparent POLCA-based work authorization.",
      category: "modern-frameworks",
      source: "VUCA Framework - Traditional Business Model"
    }
  ];

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'rajan-suri', label: '🏆 Rajan Suri - Creator\'s Authentic QRM' },
    { value: 'qrm-institute', label: '🏛️ QRM Institute - Official Authority' },
    { value: 'case-studies', label: '📈 Real-World QRM Success Cases' },
    { value: 'agriculture-food', label: '🌱 Agriculture & Food Industry' },
    { value: 'agriculture-food-advanced', label: '🌾 Advanced Agriculture & Food Intelligence' },
    { value: 'metalworking', label: '🔧 Metalworking Industry' },
    { value: 'modern-frameworks', label: '🔧 BANI/TUNA Modern Frameworks' },
    { value: 'qrm-fundamentals', label: 'QRM Fundamentals' },
    { value: 'qrm-philosophy', label: 'QRM Philosophy' },
    { value: 'qrm-concepts', label: 'QRM Concepts' },
    { value: 'qrm-teams', label: 'QRM Teams' },
    { value: 'qrm-organization', label: 'QRM Organization' },
    { value: 'qrm-leadership', label: 'QRM Leadership' },
    { value: 'qrm-gold', label: 'QRM Gold Expert Level' },
    { value: 'qrm-finance', label: 'QRM Gold Finance' },
    { value: 'qrm-buffering', label: 'QRM Gold Buffering' },
    { value: 'qrm-strategic', label: 'QRM Gold Strategic' },
    { value: 'qrm-implementation', label: 'QRM Implementation Phases' },
    { value: 'qrm-software', label: 'QRM Software & Technology' },
    { value: 'qrm-operational', label: 'QRM Gold Operational' },
    { value: 'qrm-advanced', label: 'QRM Gold Advanced Mathematics' },
    { value: 'mct-tools', label: 'MCT Tools & Methodology' },
    { value: 'qrm-tools', label: 'QRM Gold Tools' },
    { value: 'qrm-metrics', label: 'QRM Metrics' },
    { value: 'qrm-mathematics', label: 'QRM Mathematics' },
    { value: 'system-dynamics', label: 'System Dynamics' },
    { value: 'process-measurement', label: 'Process Measurement' },
    { value: 'company-wide', label: 'Company-Wide Implementation' },
    { value: 'change-management', label: 'Change Management' },
    { value: 'lean-tools', label: 'Lean Tools' },
    { value: 'vsm-concepts', label: 'VSM Concepts' },
    { value: 'production-control', label: 'Production Control' },
    { value: 'supply-chain', label: 'Supply Chain' },
    { value: 'implementation', label: 'Implementation' },
    { value: 'systems', label: 'Systems & Technology' },
    { value: 'financial', label: 'Financial Terms' }
  ];

  const filteredVocabulary = vocabulary.filter(item => {
    const matchesSearch = searchTerm === '' || 
      item.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.definition.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryColor = (category) => {
    switch(category) {
      case 'rajan-suri': return 'bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-900 border-yellow-400 font-bold shadow-lg';
      case 'qrm-institute': return 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-900 border-blue-400 font-bold shadow-lg';
      case 'case-studies': return 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-900 border-green-400 font-bold shadow-lg';
      case 'agriculture-food': return 'bg-gradient-to-r from-green-100 to-lime-100 text-green-900 border-green-400 font-bold shadow-lg';
      case 'agriculture-food-advanced': return 'bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-900 border-emerald-400 font-bold shadow-lg';
      case 'metalworking': return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-900 border-gray-400 font-bold shadow-lg';
      case 'modern-frameworks': return 'bg-gradient-to-r from-orange-100 to-red-100 text-orange-900 border-orange-400 font-bold shadow-lg';
      case 'qrm-fundamentals': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'qrm-philosophy': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'qrm-concepts': return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case 'qrm-teams': return 'bg-violet-100 text-violet-800 border-violet-200';
      case 'qrm-organization': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'qrm-leadership': return 'bg-pink-100 text-pink-800 border-pink-200';
      case 'qrm-gold': return 'bg-yellow-100 text-yellow-800 border-yellow-300 font-bold';
      case 'qrm-finance': return 'bg-green-100 text-green-800 border-green-300 font-bold';
      case 'qrm-buffering': return 'bg-orange-100 text-orange-800 border-orange-300 font-bold';
      case 'qrm-strategic': return 'bg-red-100 text-red-800 border-red-300 font-bold';
      case 'qrm-implementation': return 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-900 border-blue-400 font-bold shadow-lg';
      case 'qrm-software': return 'bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-900 border-indigo-400 font-bold shadow-lg';
      case 'qrm-operational': return 'bg-green-100 text-green-800 border-green-300 font-bold';
      case 'qrm-advanced': return 'bg-purple-100 text-purple-800 border-purple-300 font-bold';
      case 'mct-tools': return 'bg-indigo-100 text-indigo-800 border-indigo-300 font-bold';
      case 'qrm-tools': return 'bg-gray-100 text-gray-800 border-gray-300 font-bold';
      case 'qrm-metrics': return 'bg-teal-100 text-teal-800 border-teal-200';
      case 'qrm-mathematics': return 'bg-slate-100 text-slate-800 border-slate-200';
      case 'system-dynamics': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'process-measurement': return 'bg-lime-100 text-lime-800 border-lime-200';
      case 'company-wide': return 'bg-sky-100 text-sky-800 border-sky-200';
      case 'change-management': return 'bg-rose-100 text-rose-800 border-rose-200';
      case 'lean-tools': return 'bg-green-100 text-green-800 border-green-200';
      case 'vsm-concepts': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'production-control': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'supply-chain': return 'bg-red-100 text-red-800 border-red-200';
      case 'implementation': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'systems': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'financial': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12">
      <PageSEO path="/qrm-vocabulary" />
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Quick Responsive Manufacturing Vocabulary</h1>
            <p className="text-xl text-gray-600 mb-2">
              Comprehensive glossary based on QRM Alpha, Silver & Gold training
            </p>
            <p className="text-lg text-gray-500">
              Authoritative definitions from Rajan Suri masterclasses and professional training materials
            </p>
          </div>

          {/* Training Source Banner */}
          <Card className="p-6 mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <div className="flex items-center justify-center space-x-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">QRM Alpha</div>
                <div className="text-sm text-gray-600">Foundation Training</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">QRM Silver</div>
                <div className="text-sm text-gray-600">Advanced Concepts</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">QRM Gold</div>
                <div className="text-sm text-gray-600">Expert Level</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">Rajan Suri</div>
                <div className="text-sm text-gray-600">Masterclasses</div>
              </div>
            </div>
          </Card>

          {/* Search and Filter */}
          <Card className="p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-lg font-semibold mb-2 block">🔍 Search Terms</Label>
                <Input
                  type="text"
                  placeholder="Search vocabulary (e.g., MCT, POLCA, VSM)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="text-lg"
                />
              </div>
              <div>
                <Label className="text-lg font-semibold mb-2 block">📂 Filter by Category</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="text-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.value} value={category.value}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="mt-4 text-center">
              <Badge className="px-4 py-2 text-sm">
                {filteredVocabulary.length} terms found
              </Badge>
            </div>
          </Card>

          {/* Vocabulary Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
            {filteredVocabulary.map((item, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-all duration-300">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900 flex-1">{item.term}</h3>
                  <Badge className={`ml-3 px-3 py-1 text-xs font-medium border ${getCategoryColor(item.category)}`}>
                    {item.category.replace('-', ' ').toUpperCase()}
                  </Badge>
                </div>
                
                <p className="text-gray-700 leading-relaxed mb-4">
                  {item.definition}
                </p>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-blue-600 font-medium">
                    📚 Source: {item.source}
                  </span>
                </div>
              </Card>
            ))}
          </div>

          {/* No Results */}
          {filteredVocabulary.length === 0 && (
            <Card className="p-12 text-center">
              <div className="text-gray-400 mb-4">
                <div className="h-16 w-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🔍</span>
                </div>
              </div>
              <h3 className="text-xl font-medium text-gray-600 mb-2">No Terms Found</h3>
              <p className="text-gray-500">
                Try adjusting your search term or selecting a different category.
              </p>
            </Card>
          )}

          {/* Training Resources */}
          <Card className="p-8 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Lightbulb className="mr-3 h-6 w-6 text-green-600" />
              About These Definitions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Training Sources</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center">
                    <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                    <span>QRM Alpha Training (Foundation)</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="mr-2 h-4 w-4 text-purple-500" />
                    <span>QRM Silver Training (Advanced)</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="mr-2 h-4 w-4 text-yellow-500" />
                    <span>QRM Gold Training (Expert)</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="mr-2 h-4 w-4 text-blue-500" />
                    <span>Rajan Suri Masterclasses</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Content Quality</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center">
                    <Award className="mr-2 h-4 w-4 text-gold-500" />
                    <span>Authoritative QRM definitions</span>
                  </li>
                  <li className="flex items-center">
                    <Award className="mr-2 h-4 w-4 text-gold-500" />
                    <span>Real-world application context</span>
                  </li>
                  <li className="flex items-center">
                    <Award className="mr-2 h-4 w-4 text-gold-500" />
                    <span>Progressive learning structure</span>
                  </li>
                  <li className="flex items-center">
                    <Award className="mr-2 h-4 w-4 text-gold-500" />
                    <span>Expert-validated content</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-white rounded-lg border border-green-200">
              <p className="text-sm text-gray-700 italic">
                <strong>Content Note:</strong> All definitions and explanations are based on official QRM training materials 
                and Rajan Suri's authoritative teachings. This ensures accuracy and alignment with proper QRM methodology.
              </p>
            </div>
          </Card>

          {/* Navigation */}
          <div className="text-center mt-12">
            <div className="space-x-4">
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/business-case'}
                className="px-8 py-3"
              >
                ← EBITDA Calculator
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/implementation-costs'}
                className="px-8 py-3"
              >
                Implementation Costs
              </Button>
              <Button 
                onClick={() => window.location.href = '/'}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700"
              >
                Back to Home
              </Button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default QRMVocabulary;
